import { MiDetailFields, MiSwitch } from "@miplatform/components";
import { INPUT_TYPES } from "@miplatform/constants";
import { Box, Typography } from "@mui/material";
import { FC } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { ICalendarSetData } from "../../ConfigCalendarSetList.types";

export const CloseoutTabView: FC<CloseoutTabViewProps> = ({
  form,
  extraTabProps: { isReadOnly } = {},
}: CloseoutTabViewProps) => {
  const { control, setValue, watch } = form;

  const workTemplateBases = useWatch({
    control,
    name: "workTemplateBases",
  }).filter(({ isCustom }) => !isCustom);
  const { classes } = useStyles();
  const { openOrderLimitPerUser, openAutoOrderLimitPerUser, openOrderLimitWorkBaseTemplateIds = [] } =
    watch();
  const closeoutConfigFields = [
    {
      label: "Open order limit per calendar user",
      name: "openOrderLimitPerUser",
      formControl: control,
      fieldType: INPUT_TYPES.NUMERIC,
      width: "25%",
      flexGrow: 0,
      disabled: isReadOnly,
      value: openOrderLimitPerUser,
      setValue: (value: string) => {
        setValue("openOrderLimitPerUser", Number(value), {
          shouldValidate: true,
          shouldDirty: true,
        });
      },
    },
    {
      label: "Open auto-dispatched order limit per calendar user",
      name: "openAutoOrderLimitPerUser",
      formControl: control,
      fieldType: INPUT_TYPES.NUMERIC,
      width: "25%",
      flexGrow: 0,
      disabled: isReadOnly,
      value: openAutoOrderLimitPerUser,
      setValue: (value: string) => {
        setValue("openAutoOrderLimitPerUser", Number(value), {
          shouldValidate: true,
          shouldDirty: true,
        });
      },
    },
    {
      label: "Select which order types follow the open order limit",
      name: "openOrderLimitWorkBaseTemplateIds",
      customComponent: (
        <Box>
          <Typography variant="subtitle2" className={classes.subtitle}>
            Select which order types follow the open order limit.
          </Typography>
          {workTemplateBases.map((template) => (
            <Box className={classes.configListCard} key={template.id}>
              <MiSwitch
                label={template.name}
                value={openOrderLimitWorkBaseTemplateIds.includes(
                  template.id
                )}
                handleChange={() => {
                  if (isReadOnly) return;
                  const checked =
                    !openOrderLimitWorkBaseTemplateIds.includes(
                      template.id
                    );
                  const newSelectedOrderTemplateIds = checked
                    ? [...openOrderLimitWorkBaseTemplateIds, template.id]
                    : openOrderLimitWorkBaseTemplateIds.filter(
                        (id) => id !== template.id
                      );
                  setValue(
                    "openOrderLimitWorkBaseTemplateIds",
                    newSelectedOrderTemplateIds,
                    {
                      shouldValidate: true,
                      shouldDirty: true,
                    }
                  );
                }}
                className={undefined}
              />
            </Box>
          ))}
        </Box>
      ),
      fieldType: INPUT_TYPES.CUSTOM,
      width: "100%",
      style: { marginTop: 10 },
    },
  ];

  return (
    <ConfigAccordionHeader title="Closeout Settings">
      <MiDetailFields
        detailFields={closeoutConfigFields}
        headerProps={{
          title: "Open Order Limit",
          subtitle:
            "Determine how many open orders are allowed to exist at once for each calendar user before preventing additional work from beginning.",
          variant: "text",
        }}
      />
    </ConfigAccordionHeader>
  );
};

interface CloseoutTabViewProps {
  form: UseFormReturn<ICalendarSetData>;
  extraTabProps?: {
    isReadOnly?: boolean;
  };
}

const useStyles = makeStyles()((theme) => ({
  subtitle: {
    color: theme.palette.grey.main,
  },
  configListCard: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.12)",
    flex: 1,
  },
}));
